import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Spinner, Alert, Form, Row, Col, Card } from 'react-bootstrap';
import { usersAPI, flaskServiceAPI } from '../../services/apiService';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { getTranslatedError } from '../../utils/errorTranslator';
import { revokeViewBlobUrl } from '../../utils/mhtmlView';
import MhtmlUploadPanel from '../task/MhtmlUploadPanel';
import { ConfigProvider, DatePicker } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const History = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewUrl, setViewUrl] = useState('');
  const [viewLoading, setViewLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  // Фильтры для запроса на сервер
  const [queryFilters, setQueryFilters] = useState({
    taskId: '',
    url: '',
    comment: '',
    dateFrom: '',
    dateTo: '',
    limit: 50,
  });
  // Локальные фильтры по уже загруженным данным
  const [viewFilters, setViewFilters] = useState({
    taskId: '',
    url: '',
    comment: '',
    dateFrom: '',
    dateTo: '',
  });
  const [queryFiltersOpen, setQueryFiltersOpen] = useState(false);
  const [viewFiltersOpen, setViewFiltersOpen] = useState(false);

  useEffect(() => {
    loadHistory(queryFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeTasksResponse = (data) => {
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.tasks)) return data.tasks;
    if (Array.isArray(data)) return data;
    throw new Error('Неверный формат ответа задачи: ожидается { items: [] } или { tasks: [] }.');
  };

  const loadHistory = async (appliedFilters = {}) => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit: appliedFilters.limit || 50,
        page: 1,
        ...(appliedFilters.taskId && { search: appliedFilters.taskId }),
        ...(appliedFilters.url && { url: appliedFilters.url }),
        ...(appliedFilters.comment && { comment: appliedFilters.comment }),
        ...(appliedFilters.dateFrom && { createdFrom: appliedFilters.dateFrom }),
        ...(appliedFilters.dateTo && { createdTo: appliedFilters.dateTo }),
      };
      const data = await usersAPI.getTasksHistory(params);
      const tasksList = normalizeTasksResponse(data);
      setTasks(tasksList);
    } catch (err) {
      console.error('Ошибка при загрузке истории:', err);
      const errorMessage = getTranslatedError(err, 'Неизвестная ошибка');
      setError('Ошибка при загрузке истории: ' + errorMessage);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      processing: 'warning',
      failed: 'danger',
      pending: 'secondary',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const handleDownload = async (taskId) => {
    try {
      const blob = await flaskServiceAPI.downloadResult(taskId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `protocol_${taskId.replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Ошибка при скачивании файла: ' + getTranslatedError(err, 'Неизвестная ошибка'));
    }
  };

  const handleViewDetails = async (task) => {
    try {
      const taskDetails = await flaskServiceAPI.getTaskStatus(task.id);
      console.log('Task details received:', taskDetails);
      
      // API возвращает данные в формате: { status, protocol_number, protocol_date, result: {...}, ... }
      // Нужно преобразовать в формат, который ожидает компонент
      // Используем данные из API, но если их нет, берем из исходного объекта task
      const formattedTask = {
        id: taskDetails.task_id || taskDetails.id || task.id,
        url: taskDetails.url || task.request_data?.url || task.url,
        status: taskDetails.status || task.status,
        protocol_number: taskDetails.protocol_number || task.protocol_number,
        protocol_date: taskDetails.protocol_date || task.protocol_date,
        created_at: taskDetails.created_at || task.created_at,
        completed_at: taskDetails.completed_at || task.completed_at || task.updated_at,
        error: taskDetails.error || task.error,
        comment: taskDetails.comment ?? task.comment ?? '',
        result: taskDetails.result || task.result || (taskDetails.download_url ? {
          download_url: taskDetails.download_url,
          mhtml_view_url: taskDetails.mhtml_view_url,
          mhtml_download_url: taskDetails.mhtml_download_url,
        } : null)
      };

      if (formattedTask.result) {
        formattedTask.result.mhtml_warnings = formattedTask.result.mhtml_warnings
          || taskDetails.result?.mhtml_warnings
          || [];
        formattedTask.result.mhtml_error = formattedTask.result.mhtml_error
          || taskDetails.result?.mhtml_error
          || null;
      }
      
      console.log('Formatted task:', formattedTask);
      setSelectedTask(formattedTask);
      setCommentDraft(formattedTask.comment || '');
      setShowModal(true);
    } catch (err) {
      console.error('Error loading task details:', err);
      alert('Ошибка при загрузке деталей задачи: ' + getTranslatedError(err, 'Неизвестная ошибка'));
    }
  };

  const handleSaveComment = async () => {
    if (!selectedTask) return;
    try {
      setCommentSaving(true);
      const newComment = commentDraft?.trim() || '';
      await usersAPI.updateTaskComment(selectedTask.id, newComment || null);

      // Обновляем выбранную задачу и список
      const updatedTask = { ...selectedTask, comment: newComment };
      setSelectedTask(updatedTask);
      setTasks(prev => prev.map(t => t.id === updatedTask.id ? { ...t, comment: newComment } : t));
    } catch (err) {
      alert('Не удалось сохранить комментарий: ' + getTranslatedError(err, 'Ошибка'));
    } finally {
      setCommentSaving(false);
    }
  };

  const handleViewProtocol = async (taskId) => {
    try {
      setShowViewModal(true);
      setViewLoading(true);
      revokeViewBlobUrl(viewUrl);
      setViewUrl('');

      const blobUrl = await flaskServiceAPI.fetchRawPageViewUrl(taskId);
      setViewUrl(blobUrl);
    } catch (err) {
      console.error('Error loading protocol view:', err);
      setShowViewModal(false);
      alert('Не удалось загрузить страницу: ' + getTranslatedError(err, 'Неизвестная ошибка'));
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    revokeViewBlobUrl(viewUrl);
    setShowViewModal(false);
    setViewUrl('');
  };

  const handleMhtmlUploadSuccess = (taskData) => {
    const updatedTask = {
      ...selectedTask,
      status: taskData.status || selectedTask?.status,
      protocol_number: taskData.protocol_number || selectedTask?.protocol_number,
      protocol_date: taskData.protocol_date || selectedTask?.protocol_date,
      result: {
        ...selectedTask?.result,
        ...taskData.result,
        mhtml_warnings: taskData.result?.mhtml_warnings || [],
        mhtml_error: taskData.result?.mhtml_error || null,
      },
    };
    setSelectedTask(updatedTask);
    setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? { ...t, result: updatedTask.result } : t)));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Загрузка истории...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (tasks.length === 0) {
    return (
      <Alert variant="info">
        История задач пуста. Создайте первую задачу для генерации протокола.
      </Alert>
    );
  }

  // Локальная фильтрация уже загруженных данных
  const filteredTasks = tasks.filter((task) => {
    const taskId = (task.id || '').toString().toLowerCase();
    const taskUrl = (task.request_data?.url || task.url || '').toLowerCase();
    const taskComment = (task.comment || '').toLowerCase();
    const created = task.created_at ? new Date(task.created_at) : null;

    const idMatch = !viewFilters.taskId || taskId.includes(viewFilters.taskId.toLowerCase());
    const urlMatch = !viewFilters.url || taskUrl.includes(viewFilters.url.toLowerCase());
    const commentMatch = !viewFilters.comment || taskComment.includes(viewFilters.comment.toLowerCase());

    const fromOk = !viewFilters.dateFrom || (created && created >= new Date(viewFilters.dateFrom));
    const toOk = !viewFilters.dateTo || (created && created <= new Date(viewFilters.dateTo + 'T23:59:59'));

    return idMatch && urlMatch && commentMatch && fromOk && toOk;
  });

  const handleQueryChange = (key, value) => {
    setQueryFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleQuerySubmit = () => {
    loadHistory(queryFilters);
  };

  const handleQueryReset = () => {
    const cleared = { taskId: '', url: '', comment: '', dateFrom: '', dateTo: '', limit: 50 };
    setQueryFilters(cleared);
    loadHistory(cleared);
  };

  const handleViewFilterChange = (key, value) => {
    setViewFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleViewReset = () => {
    setViewFilters({ taskId: '', url: '', comment: '', dateFrom: '', dateTo: '' });
  };

  return (
    <>
      <ConfigProvider locale={ruRU}>
        <Card className="mb-3">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Параметры запроса (сервер)</h6>
              <Button
                size="sm"
                variant="light"
                onClick={() => setQueryFiltersOpen((v) => !v)}
                className="d-flex align-items-center gap-1"
              >
                {queryFiltersOpen ? <UpOutlined /> : <DownOutlined />} Фильтры
              </Button>
            </div>
            {queryFiltersOpen && (
              <>
                <div className="d-flex gap-2 mt-3 mb-2">
                  <Button size="sm" variant="primary" onClick={handleQuerySubmit}>
                    Обновить
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={handleQueryReset}>
                    Сброс запроса
                  </Button>
                </div>
                <Row className="g-2">
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">ID задачи</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Поиск по ID"
                        value={queryFilters.taskId}
                        onChange={(e) => handleQueryChange('taskId', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">URL</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Поиск по URL"
                        value={queryFilters.url}
                        onChange={(e) => handleQueryChange('url', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">Комментарий</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Поиск по комментарию"
                        value={queryFilters.comment}
                        onChange={(e) => handleQueryChange('comment', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">С даты</Form.Label>
                      <DatePicker
                        size="small"
                        format="DD.MM.YYYY"
                        value={queryFilters.dateFrom ? dayjs(queryFilters.dateFrom) : null}
                        onChange={(date) => handleQueryChange('dateFrom', date ? date.format('YYYY-MM-DD') : '')}
                        style={{ width: '100%' }}
                        placeholder="ДД.ММ.ГГГГ"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">По дату</Form.Label>
                      <DatePicker
                        size="small"
                        format="DD.MM.YYYY"
                        value={queryFilters.dateTo ? dayjs(queryFilters.dateTo) : null}
                        onChange={(date) => handleQueryChange('dateTo', date ? date.format('YYYY-MM-DD') : '')}
                        style={{ width: '100%' }}
                        placeholder="ДД.ММ.ГГГГ"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">Лимит</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        max={200}
                        value={queryFilters.limit}
                        onChange={(e) => handleQueryChange('limit', Math.min(200, Math.max(1, Number(e.target.value) || 1)))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Локальный фильтр (по загруженным данным)</h6>
              <Button
                size="sm"
                variant="light"
                onClick={() => setViewFiltersOpen((v) => !v)}
                className="d-flex align-items-center gap-1"
              >
                {viewFiltersOpen ? <UpOutlined /> : <DownOutlined />} Фильтры
              </Button>
            </div>
            {viewFiltersOpen && (
              <>
                <div className="d-flex justify-content-end mt-3 mb-2">
                  <Button size="sm" variant="outline-secondary" onClick={handleViewReset}>
                    Сбросить фильтр
                  </Button>
                </div>
                <Row className="g-2">
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">ID задачи</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Фильтр по ID"
                        value={viewFilters.taskId}
                        onChange={(e) => handleViewFilterChange('taskId', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">URL</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Фильтр по URL"
                        value={viewFilters.url}
                        onChange={(e) => handleViewFilterChange('url', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">Комментарий</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Фильтр по комментарию"
                        value={viewFilters.comment}
                        onChange={(e) => handleViewFilterChange('comment', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">С даты</Form.Label>
                      <DatePicker
                        size="small"
                        format="DD.MM.YYYY"
                        value={viewFilters.dateFrom ? dayjs(viewFilters.dateFrom) : null}
                        onChange={(date) => handleViewFilterChange('dateFrom', date ? date.format('YYYY-MM-DD') : '')}
                        style={{ width: '100%' }}
                        placeholder="ДД.ММ.ГГГГ"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">По дату</Form.Label>
                      <DatePicker
                        size="small"
                        format="DD.MM.YYYY"
                        value={viewFilters.dateTo ? dayjs(viewFilters.dateTo) : null}
                        onChange={(date) => handleViewFilterChange('dateTo', date ? date.format('YYYY-MM-DD') : '')}
                        style={{ width: '100%' }}
                        placeholder="ДД.ММ.ГГГГ"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      </ConfigProvider>

      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>ID задачи</th>
              <th>Тип</th>
              <th>URL</th>
              <th>Комментарий</th>
              <th>Статус</th>
              <th>Дата создания</th>
              <th>Дата завершения</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.map((task) => (
              <tr
                key={task.id}
                onDoubleClick={() => handleViewDetails(task)}
                style={{ cursor: 'pointer' }}
              >
                <td>{task.id}</td>
                <td>
                  <Badge bg="info">
                    {task.task_type === 'generate_from_screenshot' ? 'Скриншот' : 'Поиск'}
                  </Badge>
                </td>
                <td>
                  {(() => {
                    const url = task.request_data?.url || task.url;
                    if (!url) return '—';
                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          maxWidth: '220px',
                          display: 'inline-block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={url}
                      >
                        {url}
                      </a>
                    );
                  })()}
                </td>
                <td>
                  {task.comment ? (
                    <span
                      title={task.comment}
                      style={{
                        display: 'inline-block',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.comment}
                    </span>
                  ) : '—'}
                </td>
                <td>{getStatusBadge(task.status)}</td>
                <td>{formatDate(task.created_at)}</td>
                <td>{formatDate(task.completed_at || task.updated_at)}</td>
                <td>
                  <div className="d-flex gap-2">
                    {task.status === 'completed' && (
                      <>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleDownload(task.id)}
                        >
                          <DownloadOutlined /> Скачать
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewProtocol(task.id)}
                        >
                          <EyeOutlined /> Просмотр
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleViewDetails(task)}
                    >
                      Детали
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Детали задачи</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask ? (
            <div>
              <p><strong>ID:</strong> {selectedTask.id}</p>
              <p>
                <strong>URL:</strong>{' '}
                {selectedTask.url ? (
                  <a
                    href={selectedTask.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={selectedTask.url}
                  >
                    {selectedTask.url}
                  </a>
                ) : (
                  '—'
                )}
              </p>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Комментарий</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  placeholder="Добавьте заметку к протоколу"
                />
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveComment}
                    disabled={commentSaving}
                  >
                    {commentSaving ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                </div>
              </Form.Group>
              <p><strong>Статус:</strong> {getStatusBadge(selectedTask.status)}</p>
              {selectedTask.protocol_number && (
                <p><strong>Номер протокола:</strong> {selectedTask.protocol_number}</p>
              )}
              {selectedTask.protocol_date && (
                <p><strong>Дата протокола:</strong> {selectedTask.protocol_date}</p>
              )}
              {selectedTask.created_at && (
                <p><strong>Создано:</strong> {formatDate(selectedTask.created_at)}</p>
              )}
              {selectedTask.completed_at && (
                <p><strong>Завершено:</strong> {formatDate(selectedTask.completed_at)}</p>
              )}
              {selectedTask.error && (
                <Alert variant="danger">
                  <strong>Ошибка:</strong> {selectedTask.error}
                </Alert>
              )}
              {selectedTask.result && (
                <div className="mt-3">
                  {(selectedTask.result.mhtml_error || selectedTask.result.mhtml_warnings?.length > 0) && (
                    <Alert variant="warning" className="py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
                      {selectedTask.result.mhtml_error && <div>{selectedTask.result.mhtml_error}</div>}
                      {selectedTask.result.mhtml_warnings?.map((warning, index) => (
                        <div key={index}>{warning}</div>
                      ))}
                    </Alert>
                  )}
                  <h6>Результаты:</h6>
                  {selectedTask.result.download_url && (
                    <Button
                      variant="success"
                      size="sm"
                      className="me-2"
                      onClick={() => handleDownload(selectedTask.id)}
                    >
                      <DownloadOutlined /> Скачать протокол
                    </Button>
                  )}
                  {selectedTask.result.mhtml_view_url && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleViewProtocol(selectedTask.id)}
                    >
                      <EyeOutlined /> Просмотреть страницу
                    </Button>
                  )}
                  {selectedTask.status === 'completed' && (
                    <MhtmlUploadPanel
                      taskId={selectedTask.id}
                      hasWarnings={
                        !!selectedTask.result.mhtml_error
                        || (selectedTask.result.mhtml_warnings?.length > 0)
                      }
                      onSuccess={handleMhtmlUploadSuccess}
                      defaultExpanded={
                        !!selectedTask.result.mhtml_error
                        || (selectedTask.result.mhtml_warnings?.length > 0)
                      }
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно для просмотра протокола */}
      <Modal
        show={showViewModal}
        onHide={closeViewModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Просмотр страницы</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '80vh' }}>
          {viewLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">Загрузка содержимого страницы...</span>
            </div>
          ) : viewUrl ? (
            <iframe
              src={viewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '600px',
              }}
              title="Просмотр страницы"
              sandbox="allow-same-origin allow-scripts"
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeViewModal}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default History;

